import type { Workflow } from '~/types/workflow.types';

// Published workflow — no open draft.
export const welcomeSeriesWorkflow: Workflow = {
    workflow_id: 'welcome_series',
    version: 1,
    status: 'published',
    published_at: '2026-05-01T00:00:00.000Z',
    deleted_at: null,
    updated_at: '2026-05-01T00:00:00.000Z',
    definition: {
        name: 'Welcome Series',
        description: 'Sends a welcome message to new subscribers',
        entry_trigger: { type: 'contact_created' },
        steps: [
            {
                step_id: 'step_1',
                type: 'send',
                audience_type: 'marketing',
                send_time: { mode: 'safe_hours' },
                message: 'Welcome to our community!',
            },
            {
                step_id: 'step_2',
                type: 'update_contact',
                operations: [
                    {
                        field: 'tag',
                        op: 'add',
                        value: 'welcomed',
                    },
                ],
            },
            {
                step_id: 'step_3',
                type: 'send_to_workflow',
                target_workflow_id: 're_engagement',
            },
        ],
    },
};

// Workflow with a published version and an open draft (v2 in progress).
export const helpRequestWorkflowPublished: Workflow = {
    workflow_id: 'help_request',
    version: 1,
    status: 'published',
    published_at: '2026-05-15T00:00:00.000Z',
    deleted_at: null,
    updated_at: '2026-05-15T00:00:00.000Z',
    definition: {
        name: 'Help Request Workflow',
        description: 'Responds to contacts requesting help',
        entry_trigger: { type: 'workflow' },
        exit_criteria: {
            filters: [
                {
                    id: 'exit_group_resolved',
                    kind: 'success',
                    expressions: [
                        { id: 'exit_expr_tag', type: 'tags', operator: 'has_any', values: ['resolved'] },
                        { id: 'exit_expr_reply', type: 'engagement_inbound_message_since_entry' },
                    ],
                },
                {
                    id: 'exit_group_optout',
                    kind: 'failure',
                    expressions: [{ id: 'exit_expr_optout', type: 'custom_attribute_exists', key: 'opted_out' }],
                },
            ],
        },
        steps: [
            {
                step_id: 'step_1',
                type: 'condition',
                branches: [
                    {
                        target_step: 'step_2',
                        criteria: [
                            {
                                id: 'grp_branch_if',
                                kind: 'neutral',
                                expressions: [
                                    {
                                        id: 'rule_help_intent',
                                        type: 'custom_attribute_value_list',
                                        key: 'intent',
                                        operator: 'in',
                                        values: ['HELP'],
                                    },
                                ],
                            },
                        ],
                    },
                    {},
                ],
            },
            {
                step_id: 'step_2',
                type: 'send',
                audience_type: 'marketing',
                send_time: { mode: 'safe_hours' },
                message: 'We are here to help!',
            },
        ],
    },
};

export const helpRequestWorkflowDraft: Workflow = {
    workflow_id: 'help_request',
    version: 2,
    status: 'draft',
    published_at: null,
    deleted_at: null,
    updated_at: '2026-06-01T00:00:00.000Z',
    definition: {
        name: 'Help Request Workflow',
        description: 'Responds to contacts requesting help — adding tag step',
        entry_trigger: { type: 'workflow' },
        steps: [
            {
                step_id: 'step_1',
                type: 'condition',
                branches: [
                    {
                        target_step: 'step_2',
                        criteria: [
                            {
                                id: 'grp_branch_if',
                                kind: 'neutral',
                                expressions: [
                                    {
                                        id: 'rule_help_intent',
                                        type: 'custom_attribute_value_list',
                                        key: 'intent',
                                        operator: 'in',
                                        values: ['HELP'],
                                    },
                                ],
                            },
                        ],
                    },
                    {},
                ],
            },
            {
                step_id: 'step_2',
                type: 'send',
                audience_type: 'marketing',
                send_time: { mode: 'safe_hours' },
                message: 'We are here to help!',
            },
            {
                step_id: 'step_3',
                type: 'update_contact',
                operations: [
                    {
                        field: 'tag',
                        op: 'add',
                        value: 'help_requested',
                    },
                ],
            },
        ],
    },
};

// Draft-only workflow — never published.
export const reEngagementWorkflow: Workflow = {
    workflow_id: 're_engagement',
    version: 1,
    status: 'draft',
    published_at: null,
    deleted_at: null,
    updated_at: '2026-06-05T00:00:00.000Z',
    definition: {
        name: 'Re-engagement Campaign',
        description: 'Targets contacts who have not engaged in 30 days',
        entry_trigger: { type: 'workflow' },
        exit_criteria: {
            filters: [
                {
                    id: 'exit_reengaged',
                    kind: 'success',
                    expressions: [
                        { id: 'exit_reply', type: 'engagement_inbound_message_since_entry' },
                        { id: 'exit_click', type: 'engagement_link_clicked_since_entry' },
                    ],
                },
                {
                    id: 'exit_unsub',
                    kind: 'failure',
                    expressions: [
                        {
                            id: 'exit_marketing',
                            type: 'contact_field',
                            field: 'marketing_status',
                            operator: 'in',
                            values: ['unsubscribed'],
                        },
                    ],
                },
            ],
        },
        steps: [
            {
                step_id: 'step_1',
                type: 'send',
                audience_type: 'marketing',
                send_time: { mode: 'safe_hours' },
                message: "We miss you! Come back and check out what's new.",
            },
        ],
    },
};

// Published workflow entered from segments — exercises the segment entry trigger.
export const segmentEntryWorkflow: Workflow = {
    workflow_id: 'vip_onboarding',
    version: 1,
    status: 'published',
    published_at: '2026-06-20T00:00:00.000Z',
    deleted_at: null,
    updated_at: '2026-06-20T00:00:00.000Z',
    definition: {
        name: 'VIP Onboarding',
        description: 'Welcomes contacts added to the VIP segments',
        entry_trigger: { type: 'segment', segment_ids: [18, 19] },
        steps: [
            {
                step_id: 'step_1',
                type: 'send',
                audience_type: 'marketing',
                send_time: { mode: 'safe_hours' },
                message: 'Welcome to the VIP program!',
            },
        ],
    },
};

// Draft-only workflow exercising every step type — wait, condition, message, compliance, update_contact, send_to_workflow, api.
export const allStepTypesWorkflow: Workflow = {
    workflow_id: 'all_step_types',
    version: 1,
    status: 'draft',
    published_at: null,
    deleted_at: null,
    updated_at: '2026-06-10T00:00:00.000Z',
    definition: {
        name: 'All Step Types',
        description: '',
        entry_trigger: {
            type: 'external_list_contact_added',
            filters: [
                {
                    id: 'grp_1781601266095_39pnwqlvi',
                    expressions: [
                        {
                            id: 'rule_1781601266095_qofysho3x',
                            type: 'contact_field',
                            field: 'state',
                            operator: 'is_not_empty',
                        },
                    ],
                },
            ],
        },
        steps: [
            {
                step_id: 'step_2',
                type: 'wait',
                duration: 1440,
            },
            {
                step_id: 'step_1',
                type: 'condition',
                branches: [
                    {
                        target_step: 'step_3',
                        criteria: [
                            {
                                id: 'grp_1781276974377_6qzjsl8i1',
                                kind: 'neutral',
                                expressions: [
                                    {
                                        id: 'rule_1781276974377_pm8vqzwvj',
                                        type: 'contact_field',
                                        field: 'lifecycle_status',
                                        operator: 'is_not_empty',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        target_step: 'step_4',
                        criteria: [
                            {
                                id: 'grp_1781276996010_uwuo6ikn0',
                                kind: 'neutral',
                                expressions: [
                                    {
                                        id: 'rule_1781276996010_hf4ts0oz6',
                                        type: 'contact_field',
                                        field: 'lifecycle_status',
                                        operator: 'is_empty',
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                step_id: 'step_3',
                type: 'send',
                audience_type: 'marketing',
                send_time: { mode: 'safe_hours' },
                message: 'Send text',
                next_step: 'step_5',
            },
            {
                step_id: 'step_4',
                type: 'compliance',
                mode: 'standard',
                marketing: 'opt_in',
                transactional: 'opt_out',
                message: 'You are now subscribed',
                next_step: 'step_6',
            },
            {
                step_id: 'step_5',
                type: 'update_contact',
                operations: [
                    {
                        field: 'tag',
                        op: 'add',
                        value: 'new_tag',
                    },
                ],
                next_step: 'step_7',
            },
            {
                step_id: 'step_6',
                type: 'send_to_workflow',
                target_workflow_id: 'help_request',
            },
            {
                step_id: 'step_7',
                type: 'api',
                api_destination_id: 'api_dest_1',
            },
        ],
        exit_criteria: {
            filters: [
                {
                    id: 'grp_1781262297996_f86wg5p14',
                    kind: 'neutral',
                    expressions: [
                        {
                            id: 'rule_1781262297996_qxllqmzgp',
                            type: 'contact_field',
                            field: 'entry_channel',
                            operator: 'is_empty',
                            values: [],
                        },
                    ],
                },
            ],
        },
    },
};
